import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Phone, MessageCircle, MapPin, ArrowLeft, CheckCircle2 } from "lucide-react";
import { AREAS, CONTACTS, SERVICES } from "@/data/content";
import ContactForm from "@/components/ContactForm";

const AreaPage = () => {
  const { slug } = useParams();
  const area = AREAS[slug];

  React.useEffect(() => {
    if (area) document.title = `${area.subtitle} · Spazzacamino Davide`;
  }, [area]);

  if (!area) return <Navigate to="/" replace />;

  return (
    <div data-testid={`area-page-${slug}`}>
      {/* HERO */}
      <section
        className="grain ember-glow"
        style={{ backgroundColor: "var(--bg-primary)" }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm mb-6 hover:text-[var(--gold-bright)]"
            style={{ color: "var(--text-secondary)" }}
            data-testid="back-home"
          >
            <ArrowLeft size={16} /> Torna alla home
          </Link>

          <span className="eyebrow">
            <MapPin size={14} className="inline mr-1.5 -mt-0.5" /> Zona {area.name}
          </span>
          <h1 className="mt-6 text-4xl md:text-6xl max-w-4xl">
            Spazzacamino a <em>{area.name}</em> e provincia.
          </h1>
          <p className="mt-6 text-lg max-w-3xl" style={{ color: "var(--text-secondary)" }}>
            {area.intro}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href={`tel:${CONTACTS.phoneRaw}`} className="btn-primary" data-testid="area-call-btn">
              <Phone size={18} /> Chiama {CONTACTS.phoneDisplay}
            </a>
            <a
              href={`https://wa.me/${CONTACTS.whatsapp}?text=${encodeURIComponent("Ciao Davide, richiedo un preventivo per la zona di " + area.name)}`}
              target="_blank" rel="noreferrer"
              className="btn-whatsapp"
              data-testid="area-whatsapp-btn"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* SERVIZI OFFERTI */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <h2 className="text-3xl md:text-4xl mb-8">
            Servizi offerti a <em>{area.name}</em>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => (
              <div
                key={s.id}
                className="flex items-start gap-3 p-4 rounded-lg card-warm"
              >
                <CheckCircle2 size={18} style={{ color: "var(--gold-bright)" }} className="flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>
                    {s.title}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                    {s.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMUNI */}
      <section className="py-16 md:py-20 grain" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <span className="eyebrow">Comuni serviti</span>
          <h2 className="mt-5 text-3xl md:text-4xl">
            {area.towns.length} comuni della provincia di {area.name}
          </h2>
          <p className="mt-4 text-lg" style={{ color: "var(--text-secondary)" }}>
            Copertura completa. Ecco l'elenco dei comuni dove operiamo regolarmente:
          </p>

          <div className="mt-8 columns-2 md:columns-3 lg:columns-4 gap-6">
            {area.towns.map((t) => (
              <div key={t} className="mb-2 flex items-center gap-2 break-inside-avoid">
                <MapPin size={14} style={{ color: "var(--gold-bright)" }} className="flex-shrink-0" />
                <span className="text-sm" style={{ color: "var(--text-primary)" }}>
                  Spazzacamino {t}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER AREAS */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <h2 className="text-2xl md:text-3xl mb-6">Serviamo anche</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {Object.values(AREAS).filter(a => a.slug !== slug).map((other) => (
              <Link
                key={other.slug}
                to={`/spazzacamino/${other.slug}`}
                className="card-warm p-6 flex items-center justify-between"
                data-testid={`other-area-${other.slug}`}
              >
                <div>
                  <div className="font-semibold text-lg" style={{ fontFamily: "Fraunces, serif", color: "var(--text-primary)" }}>
                    Spazzacamino {other.name}
                  </div>
                  <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
                    {other.towns.length} comuni
                  </div>
                </div>
                <div className="font-semibold" style={{ color: "var(--gold-bright)" }}>→</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contatti"
        className="py-20 grain ember-glow"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="text-center mb-10">
            <span className="eyebrow">Contatti</span>
            <h2 className="mt-5 text-3xl md:text-5xl">
              Richiedi un preventivo per <em>{area.name}</em>
            </h2>
          </div>
          <div className="card-warm p-6 md:p-8">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AreaPage;
